import { type Lang, getLang, t } from './i18n';
import { enqueue, isReplayable } from './offline';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:3000';

export type Role = 'owner' | 'staff';

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  access_token_expires_at: string;
}

const TOK_KEY = 'fm.tokens';
const ROLE_KEY = 'fm.role';
const SESSION_AT_KEY = 'fm.sessionAt';

/** Where the API lives, for the offline replayer — which cannot call api(). */
export function apiBase(): string {
  return API_BASE;
}

export function getTokens(): AuthTokens | null {
  const raw = localStorage.getItem(TOK_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthTokens;
  } catch {
    // A truncated or hand-edited value would otherwise throw on every call
    // with no recovery path. Drop it and let the app fall back to signed-out.
    localStorage.removeItem(TOK_KEY);
    return null;
  }
}
export function getRole(): Role | null {
  // Validated rather than cast: a value written by an older build (or by hand)
  // used to come back as a truthy non-role, which every route guard read as
  // "signed in as something", and the fallbacks then bounced it between two
  // guards. There are two roles; anything else is signed out.
  const raw = localStorage.getItem(ROLE_KEY);
  return raw === 'owner' || raw === 'staff' ? raw : null;
}
export function setSession(tokens: AuthTokens, role: Role): void {
  localStorage.setItem(TOK_KEY, JSON.stringify(tokens));
  localStorage.setItem(ROLE_KEY, role);
  // When the tokens we are holding right now were minted. The server creates
  // the sessions row and signs the access token in the same call, so this is
  // (bar the round trip) that row's created_at — which is the only way the
  // device list can say "this one is you". Session ids are never handed to a
  // client, and every refresh rotates the row, so there is nothing stabler to
  // match on. Written here rather than at the six sign-in call sites because
  // every one of them, and the token refresh, funnels through this function.
  localStorage.setItem(SESSION_AT_KEY, String(Date.now()));
  // Learn what this person may do before their first screen renders. Every
  // sign-in path funnels through here — six screens do, and patching each one
  // would guarantee that the seventh forgets. Fire-and-forget: the navigation
  // must not wait on it, and the layouts refresh on mount anyway.
  localStorage.removeItem(PERMS_KEY);
  void refreshPermissions();
}
export function clearSession(): void {
  localStorage.removeItem(TOK_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(PERMS_KEY);
  localStorage.removeItem(SESSION_AT_KEY);
}

/** When this browser last received tokens — see setSession. Null on a session
 *  that predates this being recorded, which the device list treats as "then I
 *  do not know which row is this one". */
export function sessionIssuedAt(): number | null {
  const raw = Number(localStorage.getItem(SESSION_AT_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : null;
}

/**
 * Sign out for real: tell the server first, then forget locally.
 *
 * Every "Sign out" button in this app used to call `clearSession()` alone,
 * which empties three localStorage keys and nothing else. The refresh token
 * stayed valid on the server for its full REFRESH_TOKEN_TTL_DAYS — so signing
 * out of a borrowed laptop, or off a phone about to be handed to somebody,
 * removed the app's memory of the session without ending it. Anyone who had
 * copied that token could still mint access tokens from it for a month.
 * `POST /v1/auth/logout` has existed the whole time and nothing called it.
 *
 * Best-effort, and in this order on purpose: local state goes first so the
 * person is signed out of this browser the instant they ask, whatever the
 * network is doing. The revoke then runs against the token we captured — a
 * failure, a timeout or no signal at all cannot leave somebody still signed
 * in here. A raw `fetch` rather than `api()`, because api() would route a
 * failure into the offline replay queue and the 401 sign-in redirect, and
 * neither is right for a request whose whole job is to end this session.
 */
export async function endSession(): Promise<void> {
  const refresh = getTokens()?.refresh_token;
  clearSession();
  if (!refresh) return;
  try {
    await fetch(`${API_BASE}/v1/auth/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
      // The tab is usually navigating away a tick later; keepalive lets the
      // browser finish the request anyway rather than cancelling it.
      keepalive: true,
    });
  } catch {
    // Offline, or the server is down. The person is already signed out of
    // this browser; the token dies on its own expiry, and any refresh they
    // do not make cannot revive it. Nothing to tell them.
  }
}

/**
 * What this person may do beyond their own work.
 *
 * The owner implicitly has all three. For staff each is a switch the owner
 * flips per person — and until now those switches granted abilities that had
 * no screen: the server accepted the writes, the app never offered them.
 *
 * Cached in localStorage so a reload doesn't blank the navigation for a
 * moment, and refreshed from `GET /v1/me` on every sign-in and app boot. The
 * cache only decides what to *offer*; the server checks the row on every
 * request, so a revoked permission still bites immediately — the worst a
 * stale cache can do is show a tab that answers 403.
 */
export interface ActorPermissions {
  assign_work: boolean;
  manage_staff: boolean;
  see_money: boolean;
}

const PERMS_KEY = 'fm.perms';
const NO_PERMISSIONS: ActorPermissions = {
  assign_work: false,
  manage_staff: false,
  see_money: false,
};
const ALL_PERMISSIONS: ActorPermissions = {
  assign_work: true,
  manage_staff: true,
  see_money: true,
};

export function getPermissions(): ActorPermissions {
  if (getRole() === 'owner') return ALL_PERMISSIONS;
  const raw = localStorage.getItem(PERMS_KEY);
  if (!raw) return NO_PERMISSIONS;
  try {
    return { ...NO_PERMISSIONS, ...(JSON.parse(raw) as Partial<ActorPermissions>) };
  } catch {
    return NO_PERMISSIONS;
  }
}

export function setPermissions(p: ActorPermissions): void {
  localStorage.setItem(PERMS_KEY, JSON.stringify(p));
}

/** Pull the current truth from the server. Safe to call on boot. */
export async function refreshPermissions(): Promise<ActorPermissions> {
  try {
    const me = await api<{ permissions: ActorPermissions }>('/v1/me');
    setPermissions(me.permissions);
    return me.permissions;
  } catch {
    return getPermissions();
  }
}

/**
 * One shared refresh attempt, however many requests 401 around the same
 * moment.
 *
 * A screen with several panels can fire half a dozen authenticated calls in
 * the same tick; if the access token has just expired they all come back
 * 401 together. Each independently trading the refresh token would mean the
 * first response rotates it (see the backend's refreshSession) and every
 * other in-flight attempt then presents a token that is already dead —
 * turning one recoverable expiry into a guaranteed sign-out. Every caller
 * that sees a 401 while `refreshing` is set awaits the same promise instead
 * of starting its own; the module-level variable is cleared (success or
 * failure) as soon as that shared attempt settles, so the next expiry after
 * this one is free to start a fresh attempt.
 */
let refreshing: Promise<AuthTokens | null> | null = null;

/**
 * Exported for the offline replayer, which cannot go through api(): its
 * whole job is to send a request that was authored under a token that has
 * since expired, and a queued clock-in must not be lost to that.
 */
export async function refreshTokens(): Promise<AuthTokens | null> {
  const tokens = getTokens();
  if (!tokens?.refresh_token) return null;
  try {
    const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { tokens: AuthTokens };
    const role = getRole();
    // setSession also kicks off refreshPermissions() — fine here: it's
    // fire-and-forget and the same thing sign-in already does.
    if (role) setSession(body.tokens, role);
    return body.tokens;
  } catch {
    // Network error, malformed body, etc. — treated the same as a refresh
    // that the server rejected: fall through to the existing sign-out path.
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * True when a write failed only because it was queued for offline replay
 * (see offline.ts) — not a real failure. A caller that treats this like any
 * other error tells the person the opposite of what happened: their tap
 * worked, was safely queued, and will land once signal returns.
 */
export function isQueuedOffline(e: unknown): e is ApiError {
  return e instanceof ApiError && e.code === 'QUEUED_OFFLINE';
}

/**
 * The same call as `api`, but the response comes back as a file rather than
 * JSON. An `<a href>` to a protected endpoint arrives without the bearer token
 * and downloads a 401, so a download has to go through fetch like everything
 * else.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const tokens = getTokens();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: tokens ? { authorization: `Bearer ${tokens.access_token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'DOWNLOAD_FAILED', t('Loading failed'));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  return apiInner(path, init, /* allowRefresh */ true);
}

async function apiInner<T = unknown>(
  path: string,
  init: RequestInit,
  allowRefresh: boolean,
): Promise<T> {
  const tokens = getTokens();
  // Three branches for the content-type header:
  //   1. No body at all (DELETE, body-less POST) → don't set content-type.
  //      Fastify rejects a request with content-type: application/json AND
  //      an empty body ("Body cannot be empty when content-type is set to
  //      'application/json'"), which is what made disable-staff and
  //      delete-receipt look like server-side 500s. They were 400s thrown
  //      before the route handler ever ran.
  //   2. FormData body → let the browser set the boundary-bearing header.
  //   3. Everything else (string / JSON body) → application/json.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  const hasBody = init.body !== undefined && init.body !== null && init.body !== '';
  const headers: Record<string, string> = {
    ...(hasBody && !isFormData ? { 'content-type': 'application/json' } : {}),
    ...(init.headers as Record<string, string> | undefined),
  };
  if (tokens) headers.authorization = `Bearer ${tokens.access_token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch (networkError) {
    // The network, not the server. A tap that fails in a basement must not read
    // as "this app does not work" — the writes that are safe to repeat are kept
    // and replayed when signal comes back.
    const method = (init.method ?? 'GET').toUpperCase();
    if (isReplayable(path, method)) {
      await enqueue({
        path,
        method,
        body: typeof init.body === 'string' ? init.body : null,
        label: path,
      });
      throw new ApiError(0, 'QUEUED_OFFLINE', t('Saved — it will send when you have signal.'));
    }
    throw networkError;
  }

  if (res.status === 401 && tokens && !path.startsWith('/v1/auth/')) {
    // A protected call went stale. Before treating this as "session over",
    // try once to trade the refresh token for a new access token and repeat
    // this exact request — this is the difference between an access token
    // expiring every ACCESS_TOKEN_TTL_SECONDS (15 minutes by default) being
    // invisible to the person mid-form, versus being logged out every 15
    // minutes. `allowRefresh` is false only on the retry itself, so a
    // request can be bounced through this path at most once.
    if (allowRefresh) {
      refreshing ??= refreshTokens().finally(() => {
        refreshing = null;
      });
      const refreshed = await refreshing;
      if (refreshed) {
        return apiInner<T>(path, init, /* allowRefresh */ false);
      }
    }

    // Refresh was not attempted (already retried once) or did not succeed —
    // this is a genuinely dead session. Wipe and bounce the user to the
    // appropriate login screen so they get a fresh sign-in instead of a
    // confusing screenful of red.
    // Auth endpoints themselves are excluded — a 401 from signin or
    // change-password is a credential problem, not a stale-token problem,
    // and the calling screen knows how to display it.
    const role = getRole();
    clearSession();
    const dest = role === 'staff' ? '/staff/login' : '/login';
    if (window.location.pathname !== dest) {
      // Carry the address across the sign-in, same contract as the route
      // guards': a session that expired while somebody was reading a task
      // should not cost them the task.
      const here = `${window.location.pathname}${window.location.search}`;
      window.location.assign(here === '/' ? dest : `${dest}?next=${encodeURIComponent(here)}`);
    }
    throw new ApiError(401, 'UNAUTHORIZED', t('Sign-in required'));
  }

  if (res.status === 204) return undefined as T;
  let json: unknown;
  const text = await res.text();
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const e = (json as { error?: { code: string; message: string } } | undefined)?.error ?? {
      code: 'UNKNOWN',
      // A non-JSON error body is a proxy/gateway page (a 502's HTML, a stack
      // trace), not a sentence for a person. Only show a bare text body when
      // it is short and plainly prose; otherwise say something human.
      message: text && text.length <= 140 && !/[<{}]/.test(text) ? text : t('Loading failed'),
    };
    throw new ApiError(res.status, e.code, e.message);
  }
  return json as T;
}

// Domain helpers
export type OperationState =
  | 'draft'
  | 'scheduled'
  | 'sent'
  | 'seen'
  | 'in_progress'
  | 'waiting_proof'
  | 'waiting_approval'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  // Set only by the server's lateness pass. Until now these two existed in the
  // database enum and nothing could ever reach them, which is why the app has
  // never once noticed that work went out and did not come back.
  | 'overdue'
  | 'escalated';

export type OperationPriority = 'normal' | 'important' | 'emergency';

export interface Operation {
  id: string;
  title: string;
  description: string | null;
  state: OperationState;
  priority: OperationPriority;
  assigneeUserId: string | null;
  scheduledFor: string | null;
  dueAt: string | null;
  locationText: string | null;
  proofRequired: boolean;
  /** Which kinds of proof the owner will actually accept — see registerProof
   *  in apps/api/src/operations/service.ts, which rejects any other kind
   *  with 422 PROOF_TYPE_NOT_ALLOWED. Empty/undefined when proofRequired is
   *  false. */
  proofTypes?: ('photo' | 'voice' | 'text')[];
  createdAt: string;
  updatedAt?: string;
  // The evening this task exists for, denormalised onto the list rows so a
  // card can say so without a second request. Null on a standalone task.
  event_title?: string | null;
  event_starts_at?: string | null;
  calendarEventId?: string | null;
  // Stamped the first time the server had to chase this. Never cleared when
  // the task is later finished — that it needed chasing is worth keeping.
  escalatedAt?: string | null;
  lastNudgeAt?: string | null;
  // Source language the author typed in (and the slot in title_translations
  // / description_translations that mirrors the canonical title/description).
  sourceLang?: string;
  // Per-language renderings populated by the server-side Haiku translator.
  // Shape: {"en": "...", "he": "...", "ur": "...", "tl": "..."}. The viewer's
  // preferred slot is selected by pickTranslation() at render time; missing
  // slots fall back to title / description (the canonical source-lang text).
  titleTranslations?: Record<string, string> | null;
  descriptionTranslations?: Record<string, string | null> | null;
  /**
   * The steps of the job, and the shopping list in the shop. Every task has
   * carried one since the table was created and no screen ever rendered it —
   * they were written and shown to nobody.
   */
  checklist?: {
    id: string;
    label: string;
    done: boolean;
    /** Per-viewer labels, filled by the same translator that does the title. */
    labelTranslations?: Record<string, string> | null;
  }[];
  // WhatsApp-style read receipt: stamped the first time the assignee opens
  // the op (POST /v1/operations/:id/mark-seen). When set, the owner UI
  // renders ✓✓ + relative time next to the assignee. When null, the row
  // shows a single ✓ "Delivered" (or nothing, if no assignee).
  firstSeenAt?: string | null;
  firstSeenByUserId?: string | null;
  // Owner-attached reference R2 keys (photos / voice / files). Distinct
  // from operation_proofs (staff completion evidence). Rendered as a
  // gallery at the top of OperationDetail and as an attachment chip on
  // task list cards.
  referenceR2Keys?: string[];
}

/**
 * One task-timer session (operation_work_sessions). Operational visibility —
 * proof of effort on a task — and deliberately never payroll. `duration_seconds`
 * is derived server-side on a stopped session; while running it is null and
 * the client renders the live elapsed time from `started_at`.
 */
export interface WorkSession {
  id: string;
  operation_id: string;
  staff_user_id: string;
  started_at: string;
  ended_at: string | null;
  status: 'running' | 'completed' | 'discarded';
  accumulated_seconds: number;
  duration_seconds: number | null;
  created_at: string;
}

/** GET /v1/operations/:id/timer — the task's sessions, the live one lifted out. */
export interface OperationTimer {
  items: WorkSession[];
  running: WorkSession | null;
}

// Note: the operations endpoints return raw drizzle rows, which means camelCase
// field names. Other endpoints (auth, money, household, me) have explicit
// serializers and use snake_case. Don't unify these yet — each is consumed by
// the screen that knows the shape.
export interface OperationUpdate {
  id: string;
  operationId: string;
  authorUserId: string;
  kind:
    | 'instruction'
    | 'reminder'
    | 'question'
    | 'answer'
    | 'note'
    | 'approval'
    | 'rejection'
    | 'issue';
  body: string;
  sourceLang: string;
  voiceR2Key: string | null;
  createdAt: string;
}

export interface OperationProof {
  id: string;
  operationId: string;
  uploaderUserId: string;
  type: 'photo' | 'receipt' | 'voice' | 'text';
  r2Key: string | null;
  textBody: string | null;
  createdAt: string;
  /** Short-lived presigned GET, attached by the detail route. Null when the
   *  proof is text, or the object store is not configured. */
  url?: string | null;
}

export interface OperationStateEvent {
  id: string;
  operationId: string;
  fromState: OperationState | null;
  toState: OperationState;
  changedByUserId: string | null;
  reason: string | null;
  createdAt: string;
}

/** The event a task belongs to, when it was created as part of one. */
export interface OperationEventRef {
  id: string;
  title: string;
  kind: string;
  starts_at: string;
  location_text: string | null;
}

/** An owner-attached reference (the "make it look like THIS" photo/file),
 *  with a short-lived presigned GET the same way proofs carry theirs. `url`
 *  is null when the object store is not configured. */
export interface OperationReferenceImage {
  key: string;
  url: string | null;
}

export interface OperationDetail {
  operation: Operation;
  updates: OperationUpdate[];
  proofs: OperationProof[];
  history: OperationStateEvent[];
  // Owner-attached reference images/files, presigned by the detail route.
  // Distinct from proofs (staff completion evidence): these are what the task
  // should end up looking like.
  referenceImages: OperationReferenceImage[];
  // Null on a task that stands on its own, which is most of them.
  event: OperationEventRef | null;
}

export interface StaffRow {
  id: string;
  full_name: string;
  username: string | null;
  preferred_lang: string;
  must_change_password: boolean;
  disabled_at: string | null;
  avatar_r2_key: string | null;
  // What this person is allowed to do beyond their own work. Granted per
  // person by the owner — this is how a senior assistant runs the house
  // without becoming a second owner.
  can_assign_work?: boolean;
  can_manage_staff?: boolean;
  can_see_money?: boolean;
  // A trial with no agreed amount is a decision, not a missing field. Payroll
  // reads this to stop nagging about a salary the owner has chosen not to set.
  on_trial?: boolean;
  // GET /v1/staff has returned these three since it was written; the type
  // stopped short of them, so the app has been carrying "driver", a phone
  // number and a start date on every row and rendering none of them.
  position?: string | null;
  phone_e164?: string | null;
  hire_date?: string | null;
}

// Which map app launches when the user taps "Navigate" on a task with an
// address. Cached locally so LocationLinks can render the right brand icon
// + deep link without forcing every embedding screen to pass it down.
// Hydrated from /v1/me at sign-in / Profile mount; persisted to the server
// via PATCH /v1/me { preferred_map }.
export type MapProvider = 'google' | 'waze' | 'apple';
const PREF_MAP_KEY = 'fm.preferredMap';
export function getPreferredMap(): MapProvider {
  const raw = localStorage.getItem(PREF_MAP_KEY);
  if (raw === 'google' || raw === 'waze' || raw === 'apple') return raw;
  return 'google';
}
export function setPreferredMap(m: MapProvider): void {
  localStorage.setItem(PREF_MAP_KEY, m);
}
export interface MoneySnapshot {
  outstanding_owed_minor: number;
  outstanding_owed_currency: string;
  pending_receipts: number;
  active_loans: number;
  // Forward-looking: what next calendar month is expected to cost the
  // household, all-in, in outstanding_owed_currency (the primary currency).
  projected_next_month_cost_minor: number;
}

// State labels — keys are English source strings, translated at render time.
// Code that wants the user-visible label should use STATE_LABEL[state] which
// returns the localized form via t(). Using a Proxy keeps this lazy: the
// active language can change without re-import.
import { t as _t } from './i18n';

const STATE_LABEL_EN: Record<OperationState, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  sent: 'Sent',
  seen: 'Seen',
  in_progress: 'In progress',
  waiting_proof: 'Waiting for proof',
  waiting_approval: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  overdue: 'Late',
  escalated: 'Still not done',
};

export const STATE_LABEL = new Proxy(STATE_LABEL_EN, {
  get(target, prop: string) {
    const raw = (target as Record<string, string>)[prop];
    return raw ? _t(raw) : prop;
  },
}) as Record<OperationState, string>;

export const STATE_PILL: Record<OperationState, string> = {
  draft: 'pill-draft',
  scheduled: 'pill-draft',
  sent: 'pill-sent',
  seen: 'pill-sent',
  in_progress: 'pill-progress',
  waiting_proof: 'pill-progress',
  waiting_approval: 'pill-progress',
  approved: 'pill-approved',
  rejected: 'pill-rejected',
  cancelled: 'pill-draft',
  overdue: 'pill-rejected',
  escalated: 'pill-rejected',
};

const PRIORITY_LABEL_EN: Record<OperationPriority, string> = {
  normal: 'Normal',
  important: 'Important',
  emergency: 'Emergency',
};
export const PRIORITY_LABEL = new Proxy(PRIORITY_LABEL_EN, {
  get(target, prop: string) {
    const raw = (target as Record<string, string>)[prop];
    return raw ? _t(raw) : prop;
  },
}) as Record<OperationPriority, string>;

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  try {
    const lang = getLang();
    const locale =
      lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
    return new Date(iso).toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
}

type RelPack = {
  now: string;
  inN: (n: number, unit: 'm' | 'h' | 'd') => string;
  agoN: (n: number, unit: 'm' | 'h' | 'd') => string;
};
const REL: Record<Lang, RelPack> = {
  en: {
    now: 'now',
    inN: (n, u) => `in ${n}${u}`,
    agoN: (n, u) => `${n}${u} ago`,
  },
  he: {
    now: 'עכשיו',
    inN: (n, u) => `בעוד ${n} ${u === 'm' ? 'ד׳' : u === 'h' ? 'ש׳' : 'י׳'}`,
    agoN: (n, u) => `לפני ${n} ${u === 'm' ? 'ד׳' : u === 'h' ? 'ש׳' : 'י׳'}`,
  },
  ur: {
    now: 'ابھی',
    inN: (n, u) => `${n} ${u === 'm' ? 'منٹ' : u === 'h' ? 'گھنٹے' : 'دن'} میں`,
    agoN: (n, u) => `${n} ${u === 'm' ? 'منٹ' : u === 'h' ? 'گھنٹے' : 'دن'} پہلے`,
  },
  tl: {
    now: 'ngayon',
    inN: (n, u) => `sa loob ng ${n}${u}`,
    agoN: (n, u) => `${n}${u} ang nakalipas`,
  },
};

export function fmtRelative(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const mins = Math.floor(abs / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  const pack = REL[getLang()];
  if (mins < 1) return pack.now;
  if (mins < 60) return future ? pack.inN(mins, 'm') : pack.agoN(mins, 'm');
  if (hrs < 24) return future ? pack.inN(hrs, 'h') : pack.agoN(hrs, 'h');
  if (days < 7) return future ? pack.inN(days, 'd') : pack.agoN(days, 'd');
  return fmtDateTime(iso);
}

// The household's own currency, cached like the language so every screen
// formats money the way this house actually thinks about it. Hydrated from
// /v1/household; AED is the fallback only until that first load lands.
const CURRENCY_KEY = 'fm.currency';
export function getCurrency(): string {
  return localStorage.getItem(CURRENCY_KEY) ?? 'AED';
}
export function setCurrency(c: string): void {
  if (c) localStorage.setItem(CURRENCY_KEY, c.toUpperCase());
}

export function fmtMoney(minor: number, currency = getCurrency()): string {
  const major = minor / 100;
  try {
    return new Intl.NumberFormat(getLang() === 'he' ? 'he-IL' : 'en-AE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    // Unknown/garbled ISO code — show the number rather than throwing.
    return `${Math.round(major).toLocaleString()} ${currency}`;
  }
}
